import { useState, useEffect } from "react";
import { X, ZoomIn, ArrowLeft } from "lucide-react";

const FRONT = "/card-front.png";
const BACK = "/card-back.png";

export function CardShowcase({ className = "" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) { document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden"; }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <button
        type="button"
        data-testid="card-showcase"
        onClick={() => setOpen(true)}
        aria-label="View the sharing card front and back larger"
        className={`group relative block w-full cursor-zoom-in select-none rounded-3xl bg-[#FBF7EE] p-6 sm:p-8 ring-1 ring-[#EAdfc9] ${className}`}
      >
        <div className="relative w-full aspect-[1.5/1]">
          {/* subtle stack behind */}
          <div className="absolute left-[3%] bottom-[3%] w-[76%] aspect-[1.75/1] rounded-xl bg-white shadow-md ring-1 ring-black/5 rotate-[-4deg]" aria-hidden />
          <div className="absolute left-[1%] bottom-[5%] w-[76%] aspect-[1.75/1] rounded-xl bg-[#F3ECDD] shadow-sm ring-1 ring-black/5 rotate-[-2deg]" aria-hidden />
          {/* back card, offset up-right */}
          <img
            src={BACK}
            alt="Back of the I Need a Proverb sharing card"
            className="absolute right-0 top-0 w-[62%] rounded-xl shadow-xl ring-1 ring-black/10 rotate-[5deg] transition-transform duration-500 group-hover:rotate-[7deg] group-hover:translate-x-1"
          />
          {/* front card, forward */}
          <img
            src={FRONT}
            alt="Front of the I Need a Proverb sharing card"
            className="absolute left-0 bottom-0 w-[80%] rounded-xl shadow-2xl ring-1 ring-black/10 transition-transform duration-500 group-hover:-translate-y-1.5"
          />
        </div>
        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#2C4C3B]">
          <ZoomIn className="w-3.5 h-3.5" /> Click to see front &amp; back
        </span>
      </button>

      {open && (
        <div
          data-testid="card-lightbox"
          className="fixed inset-0 z-[100] bg-[#0F172A]/85 backdrop-blur-sm overflow-y-auto overscroll-contain"
          onClick={() => setOpen(false)}
        >
          {/* Sticky top control bar — always visible, large tap targets on mobile */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4">
            <button
              type="button"
              data-testid="card-lightbox-back"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F172A] px-4 sm:px-5 h-11 text-sm font-semibold shadow-lg hover:bg-[#F1F5F1] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Cards
            </button>
            <button
              type="button"
              data-testid="card-lightbox-close"
              onClick={() => setOpen(false)}
              aria-label="Close preview"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F172A] pl-4 pr-3 sm:pl-5 sm:pr-4 h-11 text-sm font-semibold shadow-lg hover:bg-[#F1F5F1] transition-colors"
            >
              Close <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-center px-4 sm:px-8 pb-10 pt-2 min-h-[calc(100%-4.5rem)]">
            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <div className="grid gap-5 sm:gap-6 md:grid-cols-2">
                <figure>
                  <img data-testid="lightbox-front" src={FRONT} alt="Front of the I Need a Proverb sharing card" className="w-full rounded-xl shadow-2xl ring-1 ring-white/10" />
                  <figcaption className="mt-2.5 text-center text-xs font-semibold uppercase tracking-[0.2em] text-[#E7CF8f]">Front</figcaption>
                </figure>
                <figure>
                  <img data-testid="lightbox-back" src={BACK} alt="Back of the I Need a Proverb sharing card" className="w-full rounded-xl shadow-2xl ring-1 ring-white/10" />
                  <figcaption className="mt-2.5 text-center text-xs font-semibold uppercase tracking-[0.2em] text-[#E7CF8f]">Back</figcaption>
                </figure>
              </div>
              <p className="mt-4 text-center text-xs text-white/60">Tap outside the cards or press Esc to close.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
